import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import OptionField from "./OptionField";
import {
  ApiError,
  ago,
  api,
  atLeast,
  type Launch,
  type LaunchOptions,
  type LaunchTarget,
  type Role,
} from "../api";

/**
 * Ask the build system to compose a version and run it.
 *
 * THE FIRST CONTROL HERE THAT CAUSES A BUILD, and several steps later, hardware
 * to move. It follows the rule the run panel already set: say what it is about
 * to do in words before it does it, offer no free text where a list will do,
 * and name the person in the record. `operator` is the rung, the same one the
 * lease and the job queue draw.
 *
 * INERT IN TWO DIFFERENT WAYS, and telling them apart is the whole reason this
 * panel is not just a disabled button. "This project has no launch target" is
 * somebody's to configure; "the server has no token" is the operator's. Both
 * are a 503 from the same endpoint, and a UI that said "unavailable" would send
 * the wrong person looking.
 *
 * NO CLIENT-SIDE FORMAT CHECK. The server owns which characters a value may
 * carry and its refusal names the key; a second copy of that rule here is one
 * that drifts, and this codebase has spent the day fixing exactly that. What is
 * checked here is presence, which is data the server sent (`required`), not a
 * rule reimplemented.
 */
export default function LaunchPanel({
  projectId,
  launch,
  role,
}: {
  projectId: number;
  launch?: LaunchTarget;
  role: Role | null;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /** The refusal, and the record it still left behind. */
  const [failed, setFailed] = useState<number | null>(null);
  const [done, setDone] = useState<{ url: string; id?: number } | null>(null);
  const [history, setHistory] = useState<Launch[]>([]);
  const [options, setOptions] = useState<Record<string, LaunchOptions>>({});

  const loadHistory = useCallback(async () => {
    try {
      setHistory((await api.launches(projectId)).launches);
    } catch {
      /* the panel is worth having without its history */
    }
  }, [projectId]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    // AFTER the panel, never before it. The route reaches out to another
    // service, so it is slow and it fails on its own; any failure - including
    // a 404 from a server that predates it - is simply "no list", which is a
    // state this panel already had to handle.
    let alive = true;
    api
      .launchOptions(projectId)
      .then((d) => alive && setOptions(d.options ?? {}))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [projectId]);

  const configured = !!launch && !!launch.project;
  const may = atLeast(role, "operator");
  const vars = launch?.variables ?? [];
  const missing = vars.filter((v) => v.required && !(values[v.key] ?? "").trim());

  async function go() {
    setBusy(true);
    setError("");
    setFailed(null);
    setDone(null);
    try {
      const filled = Object.fromEntries(
        Object.entries(values).filter(([, v]) => v.trim() !== ""),
      );
      const r = await api.launchProject(projectId, filled, reason.trim());
      setDone({ url: r.pipeline_url || "", id: r.pipeline_id });
      setReason("");
    } catch (e) {
      // A REFUSAL ARRIVES AS A THROW, not as a 2xx carrying `error` - which is
      // what the first draft of this waited for, so the branch was dead and
      // its test asserted a shape the server never sends. The build system
      // saying no is a 502, and the attempt is recorded anyway: the id comes
      // back in the body so this can say which record it became, rather than
      // leaving the reader to wonder whether anything happened at all.
      setError(e instanceof ApiError ? e.message : String(e));
      const id = e instanceof ApiError ? e.body.launch_id : undefined;
      setFailed(typeof id === "number" ? id : null);
    } finally {
      // Either way: a refused attempt is in the history too.
      await loadHistory();
      setBusy(false);
    }
  }

  return (
    <section className="mt-6">
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("launch.title")}
      </h2>

      {!configured ? (
        <p className="mt-2 rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-500 dark:border-slate-800">
          {t("launch.noTarget")}
        </p>
      ) : !launch?.ready ? (
        <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <Trans
            i18nKey="launch.noToken"
            values={{ env: launch?.token_env || "?" }}
            components={{ code: <code className="font-mono" /> }}
          />
        </p>
      ) : (
        <div className="mt-2 space-y-3 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs text-slate-500">
            <Trans
              i18nKey="launch.target"
              values={{ project: launch.project, ref: launch.ref || "main" }}
              components={{ code: <code className="font-mono" /> }}
            />{" "}
            {/* WHICH RIG IT LANDS ON: not chosen here, and not knowable here
                either - the pipeline decides. Said out loud because the other
                way to start a run picks the rig and not the version, so a
                reader wanting a particular version on a particular rig gets
                half an answer from each screen and no hint that the other
                exists. */}
            {t("launch.whichRig")}
          </p>

          {vars.map((v) => (
            <OptionField
              key={v.key}
              label={v.label || v.key}
              options={options[v.key]}
              value={values[v.key] ?? ""}
              onChange={(x) => setValues({ ...values, [v.key]: x })}
              placeholder={v.example}
              required={v.required}
            />
          ))}

          <input
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-800"
            placeholder={t("launch.reason")}
            aria-label={t("launch.reason")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          <div className="flex flex-wrap items-center gap-3">
            <button
              className="rounded bg-brand-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              disabled={busy || !may || missing.length > 0}
              onClick={go}
            >
              {busy ? t("launch.starting") : t("launch.start")}
            </button>
            <span className="text-xs text-slate-500">
              {!may
                ? t("launch.needOperator")
                : missing.length > 0
                  ? t("launch.needFields", {
                      fields: missing.map((v) => v.label || v.key).join(", "),
                    })
                  : t("launch.cost")}
            </span>
          </div>

          {error && (
            <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
              {error}
              {failed !== null && (
                <span className="mt-1 block text-xs opacity-80">
                  {t("launch.recorded", { id: failed })}
                </span>
              )}
            </p>
          )}
          {done && (
            <p className="rounded-md border border-success-300 bg-success-50 px-3 py-2 text-sm text-success-900 dark:border-success-900 dark:bg-success-950 dark:text-success-200">
              {done.url ? (
                <a className="underline" href={done.url} target="_blank" rel="noreferrer noopener">
                  {t("launch.opened", { id: String(done.id ?? "") })}
                </a>
              ) : (
                t("launch.opened", { id: String(done.id ?? "") })
              )}
            </p>
          )}
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-slate-500">
            {t("launch.history", { count: history.length })}
          </summary>
          <ul className="mt-1 space-y-1 text-xs text-slate-500">
            {history.map((l) => (
              <li key={l.id} className="flex flex-wrap items-baseline gap-x-2">
                <span className="font-mono">{ago(l.created_at)}</span>
                <b>{l.requested_by}</b>
                <span className="font-mono">
                  {Object.entries(l.variables)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(" ")}
                </span>
                {l.reason && <span>— {l.reason}</span>}
                {l.pipeline_url ? (
                  <a className="underline" href={l.pipeline_url}
                     target="_blank" rel="noreferrer noopener">
                    #{l.pipeline_id}
                  </a>
                ) : (
                  // A refused launch is recorded too, and its reason is the
                  // build system's own words.
                  <span className="text-red-700 dark:text-red-400">{l.error}</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
